// Push Notification Manager for Trade Blindfolded
import { BACKEND_URL } from '../config';

const VAPID_PUBLIC_KEY = 'BIkeJcOXK1ZMdD_zqhzwu4lykiKx3Zo1G92KD69Efy0Q9Ih0NfuS0n0IpRQ-lmMOL31pJ288eu72GN1H5aP8trs';

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// Get current permission status
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return false;
  }
  
  try {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
}

// Register service worker
export async function registerServiceWorker() {
  if (!isPushSupported()) {
    console.warn('Service workers not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

// `serviceWorker.ready` never resolves when no worker is registered yet — cap the wait.
function _withTimeout(promise, ms, message) {
  return Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error(message)), ms))]);
}

// Existing registration without waiting forever (null when none yet).
async function _currentRegistration() {
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return null;
  if (reg.active) return reg;
  return _withTimeout(navigator.serviceWorker.ready, 8000, 'Service worker is still starting — please tap again in a moment.');
}

// Compare an existing subscription's applicationServerKey (ArrayBuffer) with
// the current VAPID key so we can detect a stale key-rotation mismatch.
function _appKeysMatch(existingKey, currentKeyUint8) {
  if (!existingKey) return false;
  const a = new Uint8Array(existingKey);
  if (a.length !== currentKeyUint8.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== currentKeyUint8[i]) return false;
  return true;
}

// Transient edge/origin errors are NOT app bugs — Cloudflare returns 520-524
// (and gateways 502/503/504) when the origin briefly drops or resets the
// connection. A single toggle should survive one blip, so retry with backoff.
// Two extra guards: (a) if the edge returns an HTML error page, never surface
// that raw markup to the user — translate it to a clean, actionable message;
// (b) treat a network-level throw the same as a transient status.
const TRANSIENT_STATUSES = [502, 503, 504, 520, 521, 522, 523, 524];

function _looksLikeHtml(text) {
  const t = (text || '').trimStart().toLowerCase();
  return t.startsWith('<!doctype') || t.startsWith('<html') || t.startsWith('<?xml');
}

async function fetchWithRetry(url, options, { retries = 3, backoffMs = 800 } = {}) {
  let lastResp = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    let resp;
    try {
      resp = await fetch(url, options);
    } catch (netErr) {
      // Origin reset / offline — retry if attempts remain, else fail cleanly.
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, backoffMs * (attempt + 1)));
        continue;
      }
      throw new Error('Network error reaching the server. Check your connection and try again.');
    }
    if (resp.ok || !TRANSIENT_STATUSES.includes(resp.status)) return resp;
    lastResp = resp;
    if (attempt < retries) {
      await new Promise((r) => setTimeout(r, backoffMs * (attempt + 1)));
    }
  }
  return lastResp;
}

// Subscribe to push notifications.
// Jun 2026 hardening: (1) if the browser holds a subscription created with a
// DIFFERENT VAPID key (key rotation), reusing it silently delivers zero pushes
// and a fresh subscribe throws InvalidStateError — so drop it and re-create;
// (2) retry subscribe once after clearing a stale endpoint; (3) surface the REAL
// failure reason (SW / permission / server status) instead of a generic message.
export async function subscribeToPush() {
  try {
    // Register service worker first
    const registration = await registerServiceWorker();
    if (!registration) {
      throw new Error('Service worker could not be registered (sw.js). Reload the page and try again.');
    }

    // Wait for service worker to be ready (capped so a tap never hangs)
    await _withTimeout(navigator.serviceWorker.ready, 10000, 'Service worker is still starting — please tap again in a moment.');

    const appServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

    // Reuse an existing subscription ONLY if it matches the current VAPID key.
    let subscription = await registration.pushManager.getSubscription();
    if (subscription &&
        !_appKeysMatch(subscription.options && subscription.options.applicationServerKey, appServerKey)) {
      try { await subscription.unsubscribe(); } catch (_) { /* ignore */ }
      subscription = null;
      console.warn('Push: cleared a stale subscription bound to a different VAPID key');
    }

    if (!subscription) {
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: appServerKey
        });
        console.log('New push subscription created');
      } catch (subErr) {
        // A leftover subscription can block a fresh subscribe — clear + retry once.
        console.warn('Push: subscribe failed, clearing any stale endpoint and retrying once:', subErr);
        const stale = await registration.pushManager.getSubscription();
        if (stale) { try { await stale.unsubscribe(); } catch (_) { /* ignore */ } }
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: appServerKey
        });
        console.log('New push subscription created after clearing a stale one');
      }
    } else {
      console.log('Existing push subscription reused (key matches)');
    }

    // Send subscription to backend
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('You appear to be signed out — please log in again, then enable alerts.');
    }
    const response = await fetchWithRetry(`${BACKEND_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        subscription: subscription.toJSON()
      })
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      // Transient edge error (Cloudflare 520-524) — the origin blipped, not a
      // real rejection. Retryable, actionable message.
      if (TRANSIENT_STATUSES.includes(response.status)) {
        throw new Error(`The server is temporarily unavailable (HTTP ${response.status}). Please wait a moment and tap again.`);
      }
      // A non-transient status whose body is an HTML error page — never dump
      // raw markup into the toast; give a clean generic message instead.
      if (_looksLikeHtml(detail)) {
        throw new Error(`The server returned an unexpected response (HTTP ${response.status}). Please try again shortly.`);
      }
      throw new Error(`Server rejected the subscription (HTTP ${response.status})${detail ? ': ' + detail.slice(0, 140) : ''}`);
    }

    console.log('Push subscription saved to server');
    return subscription;

  } catch (error) {
    console.error('Error subscribing to push:', error);
    throw error;
  }
}

// Unsubscribe from push notifications.
// Jun 2026: verify the SERVER actually confirmed the unsubscribe. Previously
// this ignored the HTTP status, so a failed (e.g. 520) server call still
// unsubscribed locally and the UI falsely reported success while the backend
// kept an ACTIVE subscription and pushed to a dead endpoint. Now we always
// stop this device locally, but throw a clean warning when the server did not
// confirm so the UI can tell the user alerts may take a moment to fully stop.
export async function unsubscribeFromPush() {
  const registration = await _currentRegistration();
  const subscription = registration ? await registration.pushManager.getSubscription() : null;
  if (!subscription) return true;

  const token = localStorage.getItem('auth_token');
  let serverOk = true;
  let serverStatus = 0;
  try {
    const resp = await fetchWithRetry(`${BACKEND_URL}/api/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint
      })
    });
    serverStatus = resp ? resp.status : 0;
    serverOk = !!(resp && resp.ok);
  } catch (netErr) {
    console.warn('Push: server unsubscribe call failed:', netErr);
    serverOk = false;
  }

  // Always stop THIS device regardless of the server outcome.
  try { await subscription.unsubscribe(); } catch (_) { /* ignore */ }
  console.log('Unsubscribed locally from push notifications');

  if (!serverOk) {
    throw new Error(`Alerts are off on this device, but the server didn't confirm (HTTP ${serverStatus || 'network error'}). It may take a moment to fully stop.`);
  }
  return true;
}

// Check if user is subscribed
// ⚠️ Apr 28 2026 fix: verifies BOTH the browser-side subscription AND the
// server-side record. After a backend redeploy, the browser subscription can
// remain valid locally while the server marks `push_subscriptions.active`
// false (e.g. a 410 Gone from the push vendor). That mismatch caused users
// to see "🔔 Alerts On" in the UI while silently receiving zero pushes. If
// the server disagrees, we unsubscribe locally so the UI reflects reality
// and the re-subscribe flow picks up a fresh endpoint on next click.
export async function isSubscribed() {
  if (!isPushSupported()) return false;

  let browserSub = null;
  try {
    const registration = await _currentRegistration();
    browserSub = registration ? await registration.pushManager.getSubscription() : null;
  } catch (error) {
    console.error('Error checking browser subscription:', error);
    return false;
  }
  if (!browserSub) return false;

  // Cross-check with server. Requires auth — if no token, fall back to
  // browser-only to avoid breaking unauthenticated pages.
  const token = localStorage.getItem('auth_token');
  if (!token) return true;

  try {
    // Ask about THIS device's endpoint — another device on the account must not make this one look "on".
    const resp = await fetch(`${BACKEND_URL}/api/push/status?endpoint=${encodeURIComponent(browserSub.endpoint)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) return true; // server unreachable — trust browser
    const data = await resp.json();
    if (data && data.subscribed === false) {
      // Server lost the subscription — clean up local side so the UI
      // flips to "🔕 Alerts Off" and the user can re-subscribe.
      try { await browserSub.unsubscribe(); } catch (_) { /* ignore */ }
      console.warn('Push subscription out-of-sync: server reports inactive, unsubscribed locally');
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error verifying subscription with server:', error);
    return true; // network error — trust browser to avoid false negatives
  }
}

// Test notification (for debugging)
export function sendTestNotification() {
  if (Notification.permission === 'granted') {
    new Notification('🔔 Trade Blindfolded', {
      body: 'Test notification - Push notifications are working!',
      icon: '/logo192.png',
      tag: 'test-notification'
    });
  }
}
